import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { CircularProgress } from "@mui/material";
import arrowleft from '../../assets/arowleftv2.png';
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import ToastMessage from "utils/muialerts/righttoptoast";

const PasswordReset = () => {
    const location = useLocation();
    const navigate = useNavigate();

    const token = location.state?.token;
    const userName = location.state?.userName;

    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');

    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const [alertMessage, setAlertMessage] = useState('');
    const [severity, setSeverity] = useState('success');

    const isInvalidAccess = !token || !userName;

    const handleClose = (event, reason) => {
        if (reason === 'clickaway') return;
        setOpen(false);
    };

    useEffect(() => {
        if (isInvalidAccess) {
            setAlertMessage("Invalid access. Missing token or username.");
            setSeverity("error");
            setOpen(true);
        }
    }, [isInvalidAccess]);

    const handlePasswordReset = async () => {
        if (newPassword !== confirmPassword) {
            setAlertMessage("New password and confirmation do not match.");
            setSeverity("error");
            setOpen(true);
            return;
        }

        setLoading(true);

        try {
            const response = await fetch("https://api.prd.brainymed.com/bs/v1/users/update-expired-password", {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                },
                body: JSON.stringify({
                    old_password: currentPassword,
                    new_password: newPassword,
                    user_name: userName
                })
            });

            const data = await response.json();

            if (response.status===200) {
                setAlertMessage("Password updated successfully.");
                setSeverity("success");
                setOpen(true);
                setTimeout(() => navigate("/login"), 1500);
            } else {
                setAlertMessage(data.message || "Failed to reset password.");
                setSeverity("error");
                setOpen(true);
            }
        } catch (error) {
            setAlertMessage("Something went wrong.");
            setSeverity("error");
            setOpen(true);
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <div className="flex items-center justify-center min-h-[100vh] w-full">
                <div className="w-full bg-white sm:w-[80%] md:w-[70%] lg:w-[50%] max-w-lg md:max-w-2xl p-0 md:p-6 sm:p-6 shadow-none sm:shadow-lg md:shadow-lg md:bg-white sm:bg-white h-screen sm:h-[500px] md:h-[600px] sm:rounded-lg md:rounded-lg">
                    <div className='flex gap-8'>
                        <div className="flex items-start">
                            <img
                                src={arrowleft}
                                alt="Back Arrow"
                                className="w-5 h-5 cursor-pointer"
                                onClick={() => navigate(-1)}
                            />
                        </div>
                    </div>

                    <div className='flex flex-col justify-around h-[80%]'>
                        <label className="font-semibold text-black">Your password has expired. Please reset it below.</label>
                        <div className='w-full flex flex-col items-center justify-center gap-6 mt-4'>

                            {/* Current Password Input */}
                            <div className='w-full flex flex-col items-center'>
                                <div className='flex items-start mb-2 w-full sm:w-[50%] md:w-[60%]'>
                                    <label className="text-black">Old Password</label>
                                </div>
                                <div className='relative w-full md:w-[60%] sm:w-[60%]'>
                                    <input
                                        type={showCurrentPassword ? "text" : "password"}
                                        placeholder="Old Password"
                                        className="w-full bg-transparent p-3 rounded-lg outline-none text-gray-800 text-sm font-normal border border-gray-300"
                                        value={currentPassword}
                                        disabled={isInvalidAccess}
                                        onChange={(e) => setCurrentPassword(e.target.value)}
                                    />
                                    <span
                                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                                        className="absolute right-3 top-3 cursor-pointer"
                                    >
                                        <FontAwesomeIcon icon={showCurrentPassword ? faEyeSlash : faEye} />
                                    </span>
                                </div>
                            </div>

                            {/* New Password Input */}
                            <div className='w-full flex flex-col items-center'>
                                <div className='flex items-start mb-1 w-full sm:w-[50%] md:w-[60%]'>
                                    <label className="text-black">New Password</label>
                                </div>
                                <div className='relative w-full md:w-[60%] sm:w-[60%] mt-2'>
                                    <input
                                        type={showNewPassword ? "text" : "password"}
                                        placeholder="New Password"
                                        className="w-full bg-transparent p-3 rounded-lg outline-none text-gray-800 text-sm font-normal border border-gray-300"
                                        value={newPassword}
                                        disabled={isInvalidAccess}
                                        onChange={(e) => setNewPassword(e.target.value)}
                                    />
                                    <span
                                        onClick={() => setShowNewPassword(!showNewPassword)}
                                        className="absolute right-3 top-3 cursor-pointer"
                                    >
                                        <FontAwesomeIcon icon={showNewPassword ? faEyeSlash : faEye} />
                                    </span>
                                </div>
                            </div>

                            {/* Confirm Password Input */}
                            <div className='w-full flex flex-col items-center'>
                                <div className='flex items-start mb-1 w-full sm:w-[50%] md:w-[60%]'>
                                    <label className="text-black">Re-type New Password</label>
                                </div>
                                <div className='relative w-full md:w-[60%] sm:w-[60%] mt-2'>
                                    <input
                                        type={showConfirmPassword ? "text" : "password"}
                                        placeholder="Re-type New Password"
                                        className="w-full bg-transparent p-3 rounded-lg outline-none text-gray-800 text-sm font-normal border border-gray-300"
                                        value={confirmPassword}
                                        disabled={isInvalidAccess}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                    />
                                    <span
                                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                        className="absolute right-3 top-3 cursor-pointer"
                                    >
                                        <FontAwesomeIcon icon={showConfirmPassword ? faEyeSlash : faEye} />
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Buttons for Cancel and Save */}
                        <div className='w-full flex items-center justify-center'>
                            <div className='flex w-[60%] items-center justify-around mt-6'>
                                <button
                                    className={`rounded-full w-[120px] bg-black text-white py-2 px-4 transition duration-300 ease-in-out ${loading || isInvalidAccess ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-800'}`}
                                    disabled={loading || isInvalidAccess}
                                    onClick={handlePasswordReset}
                                >
                                    {loading ? <CircularProgress size={24} color="inherit" /> : 'Reset'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <ToastMessage
                message={alertMessage}
                severity={severity}
                open={open}
                handleClose={handleClose}
            />
        </>
    );
};

export default PasswordReset;
